import React from "react";

const CHUNK_RELOAD_KEY = "routeChunkReloadedAt";
// A second chunk failure within this window shows the error instead of reloading again
const CHUNK_RELOAD_GUARD_MS = 10000;

// A lazy page's JS / CSS file failed to download: usually a new deploy replaced the old files,
// or the network dropped while the page was opening
const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error?.message ?? ""));

const readLastReload = () => {
  try {
    return Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
};

/**
 * Catches errors thrown while a page renders (or while its lazy chunk loads), so one broken page
 * does not unmount the whole app into a blank screen. The sidebar stays usable; navigating to
 * another page (resetKey change) clears the error.
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Page crashed:", error, info?.componentStack);

    // Reload once to fetch the new files; the guard stops a reload loop if the file is really missing
    if (isChunkLoadError(error) && Date.now() - readLastReload() > CHUNK_RELOAD_GUARD_MS) {
      try {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      } catch {
        // Storage blocked: still reload once
      }
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunkError = isChunkLoadError(error);
    return (
      <div
        role="alert"
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: "calc(100vh - 4.25rem)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          background: "#F3F6FB",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            padding: "24px 20px",
            background: "#fff",
            border: "1px solid #dbe3ee",
            borderRadius: 8,
            textAlign: "center",
            color: "#1a334d",
          }}
        >
          <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>
            {chunkError ? "This page could not be downloaded" : "This page ran into a problem"}
          </h2>
          <p style={{ margin: "0 0 12px", fontSize: 14, color: "#475569" }}>
            {chunkError
              ? "A newer version may have been released, or the connection dropped. Reload to try again."
              : "Reload the page to try again. If it keeps happening, share the message below with the support team."}
          </p>
          <pre
            style={{
              margin: "0 0 16px",
              padding: "8px 10px",
              fontSize: 12,
              textAlign: "left",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
              color: "#b91c1c",
            }}
          >
            {String(error?.message || error)}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontWeight: 600,
              color: "#fff",
              background: "#006ad1",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
